import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPKR } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Receipt, Trash2 } from "lucide-react";

const STATUSES = ["Pending", "Confirmed", "Shipped", "Delivered", "Cancelled"];

const STATUS_STYLE = {
  Pending: "bg-amber-100 text-amber-800",
  Confirmed: "bg-sage/15 text-sage-dark",
  Shipped: "bg-rose/15 text-rose-dark",
  Delivered: "bg-ink/10 text-ink",
  Cancelled: "bg-red-100 text-red-700",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [toDelete, setToDelete] = useState(null); // { id, order_number } | null
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { params: { status: filter } } : {};
      const r = await api.get("/admin/orders", params);
      setOrders(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Order marked as ${status}`);
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/orders/${toDelete.id}`);
      toast.success(`Order ${toDelete.order_number} deleted`);
      setOrders((prev) => prev.filter((o) => o.id !== toDelete.id));
      setToDelete(null);
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-orders-page">
      <div className="flex justify-between items-end mb-8 gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow mb-1">Operations</div>
          <h1 className="font-display text-4xl text-ink">Orders</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="label-eyebrow !text-[10px]">Filter</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44" data-testid="order-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-border-subtle overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-cream-warm border-b border-border-subtle">
            <tr className="text-left text-[10px] uppercase tracking-widest text-ink-soft">
              <th className="p-4">Order</th>
              <th className="p-4">Customer</th>
              <th className="p-4">Items</th>
              <th className="p-4">Total</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-6 text-center text-ink-soft">Loading…</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={6} className="p-10 text-center">
                <div className="font-display text-2xl text-ink mb-1">No orders yet</div>
                <div className="text-ink-soft text-sm">Orders will appear here as they come in.</div>
              </td></tr>
            ) : orders.map((o) => (
              <tr key={o.id} className="border-b border-border-subtle last:border-0 hover:bg-cream-warm/50" data-testid={`order-row-${o.id}`}>
                <td className="p-4">
                  <div className="font-semibold text-ink">{o.order_number}</div>
                  <div className="text-xs text-ink-soft">{new Date(o.created_at).toLocaleString("en-PK")}</div>
                </td>
                <td className="p-4">
                  <div className="text-ink">{o.customer.full_name}</div>
                  <div className="text-xs text-ink-soft">{o.customer.phone}</div>
                </td>
                <td className="p-4 text-ink-soft text-sm">{o.items.reduce((s, i) => s + i.quantity, 0)}</td>
                <td className="p-4 text-ink font-semibold">{formatPKR(o.total)}</td>
                <td className="p-4">
                  <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                    <SelectTrigger className={`w-36 text-xs uppercase tracking-widest ${STATUS_STYLE[o.status] || ""}`} data-testid={`order-status-select-${o.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s.toLowerCase()}-${o.id}`}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4 text-right whitespace-nowrap">
                  <Link to={`/admin/orders/${o.id}/invoice`} className="p-2 inline-flex text-ink-soft hover:text-sage" data-testid={`view-invoice-${o.id}`} title="Invoice">
                    <Receipt size={14} />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setToDelete({ id: o.id, order_number: o.order_number })}
                    className="p-2 inline-flex text-ink-soft hover:text-red-600 transition-colors"
                    data-testid={`delete-order-btn-${o.id}`}
                    aria-label={`Delete order ${o.order_number}`}
                    title="Delete order"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-order-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete{" "}
              <span className="font-semibold text-ink">{toDelete?.order_number}</span>.
              This will also remove any AWB linked to it. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-order-cancel">
              No, keep it
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="delete-order-confirm"
            >
              {deleting ? "Deleting…" : "Yes, delete order"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
